'use client'

import srOnly from '@fukku/styles/src/srOnly.module.css'
import { IconSingle } from 'icons/components/IconSingle'
import { IconSquare01L } from 'icons/components/IconSquare01L'
import { IconSquare02L } from 'icons/components/IconSquare02L'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { scrollToProductListHook } from 'product-list/components/ProductListHook/ProductListHook'
import { useProductListAnalyticsProvider } from 'product-list/context/ProductListAnalyticsProvider/useProductListAnalyticsProvider'
import {
	useProductList,
	useProductListView,
} from 'product-list/hooks/useProductList/useProductList'
import { View } from 'product-list/types/View'
import { dataTestIds } from 'product-list/utils/testIds'
import { type ReactNode, useCallback, useMemo } from 'react'
import { cx } from 'utils/cx'
import { ViewIcon } from './ViewIcon/ViewIcon'
import styles from './Views.module.scss'

interface ViewsProps {
	readonly className?: string
	readonly onUserViewSelect?: (view: View) => void
}

type ViewData = { icon: ReactNode; label: string; dataTestId: string }

export function Views({ className, onUserViewSelect }: ViewsProps) {
	const view = useProductListView()
	const { hasInspirationalPresentation, setView, resetCount } = useProductList()

	const { onViewChange } = useProductListAnalyticsProvider()
	const { t } = useLabels()

	const labels: Record<View, string> = {
		inspirational: t('accessibility.plp.gridview.firstView.icon'),
		standard: t('accessibility.plp.gridview.secondView.icon'),
		overview: t('accessibility.plp.gridview.thirdView.icon'),
	}

	const viewIcons = useMemo<Record<View, ViewData>>(
		() => ({
			inspirational: {
				icon: <IconSingle alt={labels.inspirational} />,
				label: labels.inspirational,
				dataTestId: hasInspirationalPresentation
					? dataTestIds.productListInspirationPresentation
					: dataTestIds.productListView(View.Inspirational),
			},
			standard: {
				icon: <IconSquare01L alt={labels.standard} />,
				label: labels.standard,
				dataTestId: dataTestIds.productListView(View.Standard),
			},
			overview: {
				icon: <IconSquare02L alt={labels.overview} />,
				label: labels.overview,
				dataTestId: dataTestIds.productListView(View.Overview),
			},
		}),
		[
			labels.inspirational,
			labels.overview,
			labels.standard,
			hasInspirationalPresentation,
		]
	)

	const handleClickView = useCallback(
		(newView: View) => {
			resetCount()
			scrollToProductListHook({ isSmooth: false })

			// Using requestAnimationFrame to ensure that the scroll has started before
			requestAnimationFrame(() => {
				setView(newView)
				onViewChange?.(newView)
				onUserViewSelect?.(newView)
			})
		},
		[onViewChange, setView, onUserViewSelect, resetCount]
	)

	return (
		<fieldset className={cx(styles.viewSelector, className)}>
			<legend className={srOnly.className}>
				{t('accessibility.icon.griedview.plp.text')}
			</legend>
			{Object.entries(viewIcons).map(
				([currentView, { icon, label, dataTestId }]) => (
					<ViewIcon
						key={`view-option-${currentView}`}
						view={currentView as View}
						icon={icon}
						label={label}
						isSelected={view === currentView}
						dataTestId={dataTestId}
						onClickView={handleClickView}
					/>
				)
			)}
		</fieldset>
	)
}
